import { App } from "antd";
import {
    createContext,
    ReactNode,
    useCallback,
    useEffect,
    useState,
} from "react";

import { getConfigManifest } from "@/api/configManifest/api";
import { APP_VERSION, APP_VERSION_PERSIST, ENVIRONMENT } from "@/config";
import {
    applyUpdate,
    checkUpdate as checkUpdateSW,
    setOnNeedRefresh,
} from "@/Services/SW";
import { Maintenance } from "@/types";

type NotificationInstance = ReturnType<typeof App.useApp>["notification"];

export interface SWContextType {
    needRefresh: boolean;
    checkLoading: boolean;
    storeReset: boolean;
    applyUpdate: () => void;
    getConfig: () => void;
    checkUpdate: (notification: NotificationInstance) => Promise<void>;
    silentCheckUpdate: () => Promise<void>;
    maintenance: Maintenance | null;
    trackedUserIds: string[];
}

export const SWContext = createContext<SWContextType>({
    needRefresh: false,
    applyUpdate: () => {},
    checkUpdate: () => Promise.resolve(),
    getConfig: () => Promise.resolve(),
    silentCheckUpdate: () => Promise.resolve(),
    checkLoading: false,
    storeReset: false,
    maintenance: null,
    trackedUserIds: [],
});

export const SWProvider = ({ children }: { children: ReactNode }) => {
    const [needRefresh, setNeedRefresh] = useState(false);
    const [checkLoading, setCheckLoading] = useState(false);
    const [storeReset, setStoreReset] = useState(false);
    const [maintenance, setMaintenance] = useState<null | Maintenance>(null);
    const [trackedUserIds, setTrackedUserIds] = useState<string[]>([]);

    const checkUpdate = useCallback(
        async (notification: NotificationInstance) => {
            try {
                setCheckLoading(true);
                const hasUpdate = await checkUpdateSW();

                if (!hasUpdate) {
                    notification.info({
                        title: "Оновлень не знайдено",
                    });

                    setTimeout(() => window.location.reload(), 1000);
                }
            } catch (error) {
                notification.error({
                    title: "Не вдалося перевірити оновлення",
                });
                return;
            } finally {
                setCheckLoading(false);
            }
        },
        [],
    );

    const silentCheckUpdate = useCallback(async () => {
        try {
            await checkUpdateSW();
        } catch {
            return;
        }
    }, []);

    useEffect(() => {
        const unsubscribe = setOnNeedRefresh(() => setNeedRefresh(true));
        return unsubscribe;
    }, []);

    const getConfig = useCallback(async () => {
        try {
            const configManifest = await getConfigManifest();

            setMaintenance(configManifest.maintenance ?? null);
            setTrackedUserIds(configManifest.trackedUsers ?? []);

            const hasAppUpdate = APP_VERSION !== configManifest.version;
            const hasPersistUpdate =
                APP_VERSION_PERSIST !== configManifest["persist-version"];

            setStoreReset(hasAppUpdate && hasPersistUpdate);

            if (hasAppUpdate) {
                await silentCheckUpdate();
            }
        } catch (error) {
            console.error(error);
        }
    }, [silentCheckUpdate]);

    useEffect(() => {
        if (ENVIRONMENT === "dev-local") return;

        getConfig();
    }, [getConfig]);

    return (
        <SWContext.Provider
            value={{
                getConfig,
                applyUpdate,
                checkUpdate,
                silentCheckUpdate,
                needRefresh,
                checkLoading,
                storeReset,
                maintenance,
                trackedUserIds,
            }}
        >
            {children}
        </SWContext.Provider>
    );
};
